import React from 'react';
import { useToast } from '../../../hooks/useToast';
import {
  useGetMyHolidayRequestsQuery,
  useGetMyHolidayStatsQuery,
  useCreateHolidayRequestMutation,
  useGetBankHolidaysQuery,
} from '../../../store/slices/apiSlice';
import { HolidayStatsCard } from '../components/HolidayStatsCard';
import { HolidayRequestForm } from '../components/HolidayRequestForm';
import { HolidayRequestTable } from '../components/HolidayRequestTable';
import { UpcomingBankHolidays } from '../components/UpcomingBankHolidays';
import type { HolidayRequestFormData } from '../../../utils/types';

export const MyRequestsTab: React.FC = () => {
  const toast = useToast();
  const currentYear = new Date().getFullYear();

  // Fetch stats
  const {
    data: stats,
    isLoading: isStatsLoading,
    refetch: refetchStats,
  } = useGetMyHolidayStatsQuery({ year: currentYear });

  // Fetch requests
  const {
    data: requestsData,
    isLoading: isRequestsLoading,
    refetch: refetchRequests,
  } = useGetMyHolidayRequestsQuery({ year: currentYear });

  // Create request mutation
  const [createRequest, { isLoading: isCreating }] = useCreateHolidayRequestMutation();

  // Fetch bank holidays
  const {
    data: bankHolidays,
    isLoading: isBankHolidaysLoading,
  } = useGetBankHolidaysQuery();

  const handleSubmit = async (formData: HolidayRequestFormData) => {
    try {
      await createRequest(formData).unwrap();
      toast.success(
        'Request Submitted',
        'Your holiday request has been submitted successfully.'
      );
      // Refresh stats and requests
      refetchStats();
      refetchRequests();
    } catch (error: any) {
      const errorMessage = error?.data?.error || 'Failed to submit holiday request';
      toast.error('Submission Failed', errorMessage);
    }
  };

  const requests = requestsData?.results || [];

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <HolidayStatsCard stats={stats} isLoading={isStatsLoading} />

      {/* Form and Bank Holidays - Side by Side on Desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Request Form - Takes 2 columns */}
        <div className="lg:col-span-2">
          <HolidayRequestForm
            onSubmit={handleSubmit}
            isSubmitting={isCreating}
            remainingDays={stats?.remaining}
            bankHolidays={bankHolidays || []}
          />
        </div>

        {/* Bank Holidays - Takes 1 column */}
        <div className="lg:col-span-1">
          <UpcomingBankHolidays
            bankHolidays={bankHolidays || []}
            isLoading={isBankHolidaysLoading}
          />
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">My Holiday Requests</h3>
          <p className="text-sm text-gray-500 mt-1">
            Showing requests for {currentYear}
          </p>
        </div>
        <div className="p-6">
          <HolidayRequestTable
            requests={requests}
            isLoading={isRequestsLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default MyRequestsTab;
